// src/api/client.js
// All API calls go through here.
// Anthropic key lives on the server — never in this file.

const BASE_URL = process.env.REACT_APP_API_URL || '';

// Access token lives in memory only — never localStorage
let accessToken = null;
let refreshPromise = null;

export function setAccessToken(token) { accessToken = token; }
export function clearAccessToken() { accessToken = null; }
export function getAccessToken() { return accessToken; }

async function request(path, options = {}, retry = true) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && retry) {
    const body = await res.clone().json().catch(() => ({}));
    if (body.code === 'TOKEN_EXPIRED') {
      try {
        await refreshAccessToken();
        return request(path, options, false);
      } catch {
        clearAccessToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        throw new Error('Session expired. Please log in again.');
      }
    }
  }
  return res;
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  }).then(async (res) => {
    if (!res.ok) throw new Error('Refresh failed');
    const data = await res.json();
    accessToken = data.accessToken;
    return data.accessToken;
  }).finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export const auth = {
  async signup({ email, password, accountType }) {
    const res = await request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, accountType }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Signup failed');
    accessToken = data.accessToken;
    return data;
  },
  async login({ email, password }) {
    const res = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    accessToken = data.accessToken;
    return data;
  },
  async logout() {
    await request('/auth/logout', { method: 'POST' }).catch(() => {});
    clearAccessToken();
  },
  async tryRefresh() {
    try { await refreshAccessToken(); return true; } catch { return false; }
  },
};

export const vault = {
  async get() {
    const res = await request('/api/vault');
    if (!res.ok) throw new Error('Could not retrieve vault');
    return res.json();
  },
  async save({ encryptedData, iv, authTag }) {
    const res = await request('/api/vault', {
      method: 'PUT',
      body: JSON.stringify({ encryptedData, iv, authTag }),
    });
    if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Could not save vault'); }
    return res.json();
  },
};

export const signals = {
  async save(data) {
    const res = await request('/api/signals', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Could not save signals');
    return res.json();
  },
  async getMine(days = 7) {
    const res = await request(`/api/signals/me?days=${days}`);
    if (!res.ok) throw new Error('Could not retrieve signals');
    return res.json();
  },
  async getForClient(clientId, days = 14) {
    const res = await request(`/api/signals/client/${clientId}?days=${days}`);
    if (!res.ok) throw new Error('Could not retrieve client signals');
    return res.json();
  },
};

// AI calls — proxied through server, API key never leaves server
export const ai = {
  async chat({ messages, personaId, context }) {
    const res = await request('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, personaId, context }),
    });
    if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'AI request failed'); }
    return res.json();
  },
  async flowClosing({ personaId, flowId, prompt }) {
    const res = await request('/api/ai/flow-closing', {
      method: 'POST',
      body: JSON.stringify({ personaId, flowId, prompt }),
    });
    if (!res.ok) throw new Error('Flow closing failed');
    return res.json();
  },
  async preSessionBrief(payload) {
    const res = await request('/api/ai/pre-session-brief', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Brief generation failed');
    return res.json();
  },
};

export const coaches = {
  async getMatches() {
    const res = await request('/api/coaches/matches');
    if (!res.ok) throw new Error('Could not retrieve matches');
    return res.json();
  },
  async saveProfile(profile) {
    const res = await request('/api/coaches/profile', { method: 'POST', body: JSON.stringify(profile) });
    if (!res.ok) throw new Error('Could not save profile');
    return res.json();
  },
  async saveClientProfile(profile) {
    const res = await request('/api/coaches/client-profile', { method: 'POST', body: JSON.stringify(profile) });
    if (!res.ok) throw new Error('Could not save client profile');
    return res.json();
  },
  async selectCoach(coachId) {
    const res = await request('/api/coaches/select', { method: 'POST', body: JSON.stringify({ coachId }) });
    if (!res.ok) throw new Error('Could not select coach');
    return res.json();
  },
  async getBriefs() {
    const res = await request('/api/coaches/briefs');
    if (!res.ok) throw new Error('Could not retrieve briefs');
    return res.json();
  },
  async publishBrief() {
    const res = await request('/api/coaches/briefs/publish', { method: 'POST' });
    if (!res.ok) throw new Error('Could not publish brief');
    return res.json();
  },
  async getMyClients() {
    const res = await request('/api/coaches/my-clients');
    if (!res.ok) throw new Error('Could not retrieve clients');
    return res.json();
  },
  async getLinkId(clientId) {
    const res = await request(`/api/coaches/link/${clientId}`);
    if (!res.ok) throw new Error('Could not retrieve link');
    return res.json();
  },
  async getPendingCoaches() {
    const res = await request('/api/coaches/pending');
    if (!res.ok) throw new Error('Could not retrieve pending coaches');
    return res.json();
  },
  async confirmCoach(linkId) {
    const res = await request(`/api/coaches/pending/${linkId}/confirm`, { method: 'POST' });
    if (!res.ok) throw new Error('Could not confirm coach');
    return res.json();
  },
  async acceptBrief(briefId) {
    const res = await request(`/api/coaches/briefs/${briefId}/accept`, { method: 'POST' });
    if (!res.ok) throw new Error('Could not accept brief');
    return res.json();
  },
  async getSessionNotes(linkId) {
    const res = await request(`/api/coaches/session-notes/${linkId}`);
    if (!res.ok) throw new Error('Could not retrieve notes');
    return res.json();
  },
  async saveSessionNote(linkId, content) {
    const res = await request(`/api/coaches/session-notes/${linkId}`, {
      method: 'POST', body: JSON.stringify({ content }),
    });
    if (!res.ok) throw new Error('Could not save note');
    return res.json();
  },
  async getSharedContext(linkId) {
    const res = await request(`/api/coaches/shared-context/${linkId}`);
    if (!res.ok) throw new Error('Could not retrieve shared context');
    return res.json();
  },
  async saveSharedContext(linkId, payload) {
    const res = await request(`/api/coaches/shared-context/${linkId}`, {
      method: 'POST', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Could not save shared context');
    return res.json();
  },
};

export const messages = {
  async getThread(linkId) {
    const res = await request(`/api/messages/${linkId}`);
    if (!res.ok) throw new Error('Could not retrieve messages');
    return res.json();
  },
  async send(linkId, payload) {
    const res = await request(`/api/messages/${linkId}`, {
      method: 'POST', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Could not send message');
    return res.json();
  },
  async markRead(linkId) {
    await request(`/api/messages/${linkId}/read`, { method: 'POST' });
  },
};

export const notifications = {
  async getVapidKey() {
    const res = await request('/api/notifications/vapid-key');
    if (!res.ok) throw new Error('Push not configured');
    return res.json();
  },
  async subscribe(subscription) {
    const res = await request('/api/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription }),
    });
    if (!res.ok) throw new Error('Could not subscribe');
    return res.json();
  },
  async unsubscribe(endpoint) {
    await request('/api/notifications/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }).catch(() => {});
  },
  async getAll() {
    const res = await request('/api/notifications');
    if (!res.ok) return { notifications: [] };
    return res.json();
  },
  async markRead(ids = []) {
    await request('/api/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }).catch(() => {});
  },
};
